'use client';
import React from 'react';
import Link from 'next/link';
import { ArrowRight, Star } from 'lucide-react';
import { usePlacementCatalog } from '@/hooks/usePlacementCatalog';

export default function PricingTeaserSection() {
  const { placements } = usePlacementCatalog();

  return (
    <section className="py-20">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold text-brand-400 uppercase tracking-widest mb-3">Transparent Pricing</p>
          <h2 className="font-display text-4xl sm:text-5xl font-800 mb-4">
            <span className="text-white">Simple, Flat </span>
            <span className="text-gradient">Ad Rates</span>
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Per-region pricing. Multi-region discounts available on standard inside pages. No surprises.
          </p>
        </div>

        {/* Multi-region discount callout */}
        <div className="glass-card rounded-2xl p-5 border border-brand-600/30 mb-8 max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center shrink-0">
              <Star size={18} className="text-white" />
            </div>
            <div>
              <p className="font-semibold text-white text-sm">Multi-Region Discount on Standard Inside Pages</p>
              <p className="text-xs text-slate-400 mt-0.5">Book 3+ regions and save on standard placements. Discounts applied automatically in the order wizard.</p>
            </div>
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-10">
          {placements?.map((p) => (
            <div
              key={`pricing-${p?.name}`}
              className={`glass-card glass-card-hover rounded-2xl p-5 relative ${
                p?.premium ? 'border border-brand-600/40' : ''
              }`}
            >
              {p?.premium && (
                <span className={`absolute top-3 right-3 text-xs font-semibold px-2 py-0.5 rounded-full ${
                  p?.id === 'ifc' ? 'bg-green-500/15 text-green-400 border border-green-500/25' : 'bg-brand-600/20 text-brand-300 border border-brand-600/30'
                }`}>
                  {p?.id === 'ifc' ? 'Popular' : 'Premium'}
                </span>
              )}
              <h3 className="font-semibold text-white text-sm leading-tight mb-4 pt-7">{p?.name}</h3>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">One Issue</p>
                  <p className="font-display font-bold text-xl text-white tabular-nums">${p?.oneIssue?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">3-Issue / mo</p>
                  <p className="font-display font-bold text-lg text-brand-400 tabular-nums">${p?.threeIssue?.toLocaleString()}<span className="text-xs font-normal text-slate-500">/mo</span></p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link href="/placements" className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold btn-primary">
            View All Placements
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}